import { useEffect, useState } from "react";
import GenericDialog from "./generic";
import { deviceList, deviceSet } from "api/device";
import { Device, DeviceListReqBody, DeviceSetBody } from "api/types/device";
import { deviceConfiguration } from "models/devices/configuration";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setLoading } from "store/appSlice";
import LabeledField from "providers/labeledField";
import CircleButton from "widgets/controls/buttons/circle-button";
import DropdownButton from "widgets/controls/buttons/dropdown";
import GenericButton from "widgets/controls/buttons/generic";
import {
  MdChevronLeft,
  MdChevronRight,
  MdKeyboardDoubleArrowLeft,
  MdKeyboardDoubleArrowRight,
} from "react-icons/md";
import SitesCombobox from "widgets/shared/inputs/usersCombobox";

interface DeviceItem {
  snr: string;
  name: string;
}

const DeviceManageChildrenDialog = (props: {
  device: Device;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const { device, isOpen, onClose, onSuccess } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [selectedSite, setSelectedSite] = useState<string>();
  const [available, setAvailable] = useState<DeviceItem[]>([]);
  const [children, setChildren] = useState<DeviceItem[]>([]);
  const [selectedAvailable, setSelectedAvailable] = useState<DeviceItem[]>([]);
  const [selectedChildren, setSelectedChildren] = useState<DeviceItem[]>([]);
  const [disabled, setDisabled] = useState(true);
  const [initialChildren, setInitialChildren] = useState<string[]>([]);

  const fetchSiteDevices = async (siteId: string) => {
    dispatch(setLoading(true));
    const body: DeviceListReqBody = {
      classId: siteId,
      sort: "snr",
      desc: true,
      installed: "BOTH",
    };

    const { data, status, isLoading } = await deviceList(body);
    dispatch(setLoading(isLoading));

    if (status && status === 200) {
      setSelectedSite(siteId);
      const parentDeviceType = deviceConfiguration.getDeviceByTag(
        device.model
      )?.units;
      const deviceItems: DeviceItem[] = data
        .filter((d: Device) => d.snr !== device.snr)
        .filter((d: Device) => !children.some((child) => child.snr === d.snr))
        .filter((d: Device) => {
          const deviceType = deviceConfiguration.getDeviceByTag(d.model)?.units;
          return deviceType === parentDeviceType;
        })
        .map((d: Device) => ({ snr: d.snr, name: d.name }));
      setAvailable(deviceItems);
      setSelectedAvailable([]);
    }
  };

  useEffect(() => {
    if (isOpen && device?.children) {
      const childItems: DeviceItem[] = device.children.map((snr) => ({
        snr,
        name: snr,
      }));
      setChildren(childItems);
      setInitialChildren(device.children);
    }
  }, [isOpen, device]);

  useEffect(() => {
    const currentChildSnrs = children.map((c) => c.snr).sort();
    const initialChildSnrs = [...initialChildren].sort();
    const hasChanges =
      JSON.stringify(currentChildSnrs) !== JSON.stringify(initialChildSnrs);
    setDisabled(!hasChanges);
  }, [children, initialChildren]);

  const move = (
    source: DeviceItem[],
    targetSetter: React.Dispatch<React.SetStateAction<DeviceItem[]>>,
    selectedItems: DeviceItem[],
    sourceSetter: React.Dispatch<React.SetStateAction<DeviceItem[]>>,
    clearSelection: React.Dispatch<React.SetStateAction<DeviceItem[]>>
  ) => {
    targetSetter((prev) => [
      ...prev,
      ...selectedItems.filter((item) => !prev.some((p) => p.snr === item.snr)),
    ]);
    sourceSetter((prev) =>
      prev.filter((item) => !selectedItems.some((sel) => sel.snr === item.snr))
    );
    clearSelection([]);
  };

  const moveAll = (
    source: DeviceItem[],
    targetSetter: React.Dispatch<React.SetStateAction<DeviceItem[]>>,
    sourceSetter: React.Dispatch<React.SetStateAction<DeviceItem[]>>,
    clearSelection: React.Dispatch<React.SetStateAction<DeviceItem[]>>
  ) => {
    targetSetter((prev) => [
      ...prev,
      ...source.filter((item) => !prev.some((p) => p.snr === item.snr)),
    ]);
    sourceSetter([]);
    clearSelection([]);
  };

  const handleSubmit = async () => {
    dispatch(setLoading(true));
    const body: DeviceSetBody = {
      snr: device.snr,
      children: children.map((c) => c.snr),
    };

    const { status, isLoading } = await deviceSet(body);
    dispatch(setLoading(isLoading));

    if (status && status === 200) {
      onSuccess();
    }
  };

  const handleClose = () => {
    setSelectedSite(undefined);
    setAvailable([]);
    setChildren([]);
    setSelectedAvailable([]);
    setSelectedChildren([]);
    setInitialChildren([]);
    setDisabled(true);
    onClose();
  };

  return (
    <GenericDialog
      isOpen={isOpen}
      title={t("dialog.device.info.title.manageChildren")}
      onExit={handleClose}
      size="3xl"
    >
      <div className="mt-6 flex flex-col gap-4 rounded-lg">
        <div className="flex flex-col justify-center gap-4 rounded-xl bg-gray-100 md:flex-row md:items-center">
          {/* Left Box - Available Devices */}
          <div className="flex w-full flex-col gap-4 rounded-xl bg-gray-100 p-4">
            <div className="w-full">
              <LabeledField
                label={t("common.select.site")}
                id={"manage-children-site"}
                textColor="secondary"
              >
                <SitesCombobox
                  filter
                  id="manage-children-select-site"
                  placeholder={t("common.select.site")}
                  variant="solid"
                  color="secondary"
                  value={selectedSite}
                  onChange={(item) => {
                    fetchSiteDevices(item);
                  }}
                  sizes="md"
                  rounded="rounded-xl"
                />
              </LabeledField>
            </div>

            <div className="max-h-80 min-h-80 flex-1 overflow-y-scroll rounded border-2 p-2">
              {available.map((item) => (
                <div
                  key={item.snr}
                  className={`mb-1 ${
                    selectedAvailable.some((i) => i.snr === item.snr)
                      ? "rounded border-2 border-yellow-400 bg-yellow-100"
                      : ""
                  }`}
                >
                  <DropdownButton
                    text={`${item.name} - [ ${item.snr} ]`}
                    onClick={() =>
                      setSelectedAvailable((prev) =>
                        prev.some((i) => i.snr === item.snr)
                          ? prev.filter((i) => i.snr !== item.snr)
                          : [...prev, item]
                      )
                    }
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex min-w-10 justify-center gap-2 md:flex-col">
            <CircleButton
              onClick={() =>
                move(
                  available,
                  setChildren,
                  selectedAvailable,
                  setAvailable,
                  setSelectedAvailable
                )
              }
              icon={MdChevronRight}
              size="sm"
              extra="rounded-md justify-center"
              color="amber"
            />
            <CircleButton
              onClick={() =>
                move(
                  children,
                  setAvailable,
                  selectedChildren,
                  setChildren,
                  setSelectedChildren
                )
              }
              icon={MdChevronLeft}
              size="sm"
              extra="rounded-md justify-center"
              color="amber"
            />
            <CircleButton
              onClick={() =>
                moveAll(
                  available,
                  setChildren,
                  setAvailable,
                  setSelectedAvailable
                )
              }
              icon={MdKeyboardDoubleArrowRight}
              size="sm"
              extra="rounded-md justify-center"
              color="amber"
            />
            <CircleButton
              onClick={() =>
                moveAll(
                  children,
                  setAvailable,
                  setChildren,
                  setSelectedChildren
                )
              }
              icon={MdKeyboardDoubleArrowLeft}
              size="sm"
              extra="rounded-md justify-center"
              color="amber"
            />
          </div>

          {/* Right Box - Children */}
          <div className="flex w-full flex-col gap-4 rounded-xl bg-gray-100 p-4">
            <div className="max-h-80 min-h-80 flex-1 overflow-y-scroll rounded border-2 p-2">
              {children.map((item, index) => (
                <div
                  key={`${item.snr}-${index}`}
                  className={`mb-1 ${
                    selectedChildren.some((i) => i.snr === item.snr)
                      ? "rounded border-2 border-yellow-400 bg-yellow-100"
                      : ""
                  }`}
                >
                  <DropdownButton
                    text={`${item.name} - [ ${item.snr} ]`}
                    extra={
                      selectedChildren.some((i) => i.snr === item.snr)
                        ? "text-secondary"
                        : "text-text-800"
                    }
                    onClick={() =>
                      setSelectedChildren((prev) =>
                        prev.some((i) => i.snr === item.snr)
                          ? prev.filter((i) => i.snr !== item.snr)
                          : [...prev, item]
                      )
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 py-2">
          <GenericButton
            text={t("button.action.cancel")}
            onClick={handleClose}
            color="gray"
          />
          <GenericButton
            text={t("button.action.save")}
            onClick={handleSubmit}
            disabled={disabled}
          />
        </div>
      </div>
    </GenericDialog>
  );
};

export default DeviceManageChildrenDialog;
